import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAction, useMutation, useQuery } from "convex/react";
import { useUnsavedChanges } from "../lib/useUnsavedChanges";
import { api, type Doc, type Id } from "../lib/api";
import { routeIdParam, searchIdParam } from "../domain/routeParams";
import { caughtErrorMessage } from "../domain/errors";
import { SectionEditor } from "../components/SectionEditor";
import { RichText } from "../components/RichText";
import {
  CHANGE_TYPE_OPTIONS,
  buildSectionTree,
  changeTypeDisplayName,
  emptyLegislationEditorDialogState,
  emptyLegislationEditorFeedbackState,
  emptyLegislationStructureExtractState,
  insertStructureNodesButtonLabel,
  legislationDeleteConfirmationMessage,
  legislationEditorDialogPreparedCommand,
  legislationEditorDialogSubmitLabel,
  legislationEditorDialogTitle,
  legislationEditorFeedbackClassName,
  legislationEditorSelectedSection,
  nextLegislationEditorDialogState,
  nextLegislationEditorFeedbackState,
  nextLegislationEditorSectionId,
  nextLegislationStructureExtractState,
  sectionSiblingOrderAfterMove,
  sectionTreeRowView,
  structureExtractButtonView,
  structurePreviewNodeView,
  type LegislationEditorDialogAction,
  type LegislationEditorDialogState,
  type SectionTreeNode,
} from "../domain/legislation";
import { toFlatSection } from "../lib/adapters";
import { docToPlainText, emptyDoc, richTextDocOrEmpty, textDoc, type RichTextDoc } from "../domain/richText";
import { buildSectionMarkup } from "../domain/markupDoc";
import {
  emptyUpdateLogFormState,
  emptyUpdateLogSubmissionState,
  nextUpdateLogFormState,
  nextUpdateLogSubmissionState,
  updateLogFormSubmittedState,
  updateLogFormView,
  updateLogSubmissionError,
  type UpdateLogFormAction,
} from "../domain/updateLog";
import type { StructureNode } from "../domain/structure";
import { dateInputValueFromMs } from "../domain/time";
import type { ChangeType } from "../../convex/model/enums";


type SectionNode = SectionTreeNode<Id<"sections">>;
type PendingPublication = {
  readonly sectionId: Id<"sections">;
  readonly content: RichTextDoc;
  readonly hidden: boolean;
  readonly changeType: ChangeType;
  readonly markup: RichTextDoc | null;
};

export function LegislationEditor() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const id = routeIdParam<"legislation">(params, "id");
  const alertId = searchIdParam<"legislativeAlerts">(searchParams, "alert");

  const legislation = useQuery(api.legislation.get, id ? { id } : "skip");
  const sections = useQuery(api.sections.listByLegislation, id ? { legislationId: id } : "skip");
  const terms = useQuery(api.definedTerms.listByLegislation, id ? { legislationId: id } : "skip");
  const updates = useQuery(api.updateLog.listByLegislation, id ? { legislationId: id } : "skip");
  const linkedAlert = useQuery(api.alerts.getForEditor, alertId !== null ? { id: alertId } : "skip");

  const createSection = useMutation(api.sections.create);
  const rename = useMutation(api.sections.rename);
  const setHidden = useMutation(api.sections.setHidden);
  const reorder = useMutation(api.sections.reorder);
  const updateContent = useMutation(api.sections.updateContent);
  const defineTerm = useMutation(api.definedTerms.create);
  const removeTerm = useMutation(api.definedTerms.remove);
  const removeLegislation = useMutation(api.legislation.remove);
  const bulkInsert = useMutation(api.sections.bulkInsert);
  const extractStructure = useAction(api.ai.extractStructure);
  const draftAmendment = useAction(api.ai.draftAmendment);
  const navigate = useNavigate();

  const [structureExtractState, setStructureExtractState] = useState(() => emptyLegislationStructureExtractState<StructureNode>());
  const [editorFeedback, setEditorFeedback] = useState(() => emptyLegislationEditorFeedbackState());
  const [editorDialog, setEditorDialog] = useState(() => emptyLegislationEditorDialogState<Id<"sections">>());

  const [selectedId, setSelectedId] = useState<Id<"sections"> | null>(null);
  const [draft, setDraft] = useState<RichTextDoc>(emptyDoc());
  const baseline = useRef<RichTextDoc>(emptyDoc());
  const [logPrefill, setLogPrefill] = useState<ChangeType | null>(null);
  const [pendingPublication, setPendingPublication] = useState<PendingPublication | null>(null);

  const tree = useMemo(
    () => buildSectionTree((sections ?? []).map(toFlatSection)),
    [sections],
  );
  const sectionsRef = useRef<typeof sections>(sections);
  const selected = legislationEditorSelectedSection({ sections: sections ?? [], selectedId });
  const aiOpen = structureExtractState.open;
  const aiText = structureExtractState.sourceText;
  const aiNodes = structureExtractState.nodes;
  const aiError = structureExtractState.error;
  const extractButton = structureExtractButtonView({
    sourceText: aiText,
    busy: structureExtractState.busy,
  });

  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  useEffect(() => {
    const nextSelectedId = nextLegislationEditorSectionId({ tree, selectedId });
    if (nextSelectedId !== selectedId) setSelectedId(nextSelectedId);
  }, [tree, selectedId]);

  // Load draft when the selected section changes, not on every query refresh.
  useEffect(() => {
    if (selectedId === null) return;
    const selectedSection = legislationEditorSelectedSection({ sections: sectionsRef.current ?? [], selectedId });
    if (selectedSection !== null) {
      const content = richTextDocOrEmpty(selectedSection.content);
      setDraft(content);
      baseline.current = content;
    }
  }, [selectedId]);

  const draftChanged = JSON.stringify(draft) !== JSON.stringify(baseline.current);
  const hasUnpublishedWork = draftChanged || pendingPublication !== null;
  useUnsavedChanges(hasUnpublishedWork);

  useEffect(() => {
    if (!hasUnpublishedWork) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnpublishedWork]);

  if (id === undefined || legislation === undefined || sections === undefined) {
    return <p className="text-slate-500">Loading…</p>;
  }
  if (legislation === null) {
    return <p className="text-slate-500">Not found. <Link className="text-brand" to="/editor">Back</Link></p>;
  }

  const move = async (node: SectionNode, dir: -1 | 1) => {
    const nextOrder = sectionSiblingOrderAfterMove({ sections, sectionId: node.id, direction: dir });
    if (nextOrder.kind === "changed") await reorder({ orderedIds: nextOrder.orderedIds });
  };

  const clearEditorFeedback = () => {
    setEditorFeedback((current) => nextLegislationEditorFeedbackState(current, { type: "cleared" }));
  };

  const showEditorFailure = (message: string) => {
    setEditorFeedback((current) => nextLegislationEditorFeedbackState(current, { type: "operationFailed", message }));
  };

  const showEditorSuccess = (message: string) => {
    setEditorFeedback((current) => nextLegislationEditorFeedbackState(current, { type: "operationSucceeded", message }));
  };

  const updateEditorDialog = (action: LegislationEditorDialogAction<Id<"sections">>) => {
    setEditorDialog((current) => nextLegislationEditorDialogState(current, action));
  };

  const closeEditorDialog = () => {
    updateEditorDialog({ type: "closed" });
  };

  const addPart = () => {
    updateEditorDialog({ type: "openAddPart" });
  };

  const addSection = (parentId: Id<"sections">) => {
    updateEditorDialog({ type: "openAddSection", parentId });
  };

  const handleRename = (node: SectionNode) => {
    updateEditorDialog({
      type: "openRenameSection",
      sectionId: node.id,
      number: node.number,
      title: node.title,
    });
  };

  const handleDefineTerm = (term: string) => {
    updateEditorDialog({ type: "openDefineTerm", term });
  };

  const submitEditorDialog = async () => {
    const prepared = legislationEditorDialogPreparedCommand({ state: editorDialog, legislationId: id });
    if (prepared.kind === "none") return;
    if (prepared.kind === "invalid") {
      setEditorFeedback((current) => nextLegislationEditorFeedbackState(current, { type: "invalidCommand", error: prepared.error }));
      return;
    }

    clearEditorFeedback();
    closeEditorDialog();

    switch (prepared.kind) {
      case "createPart":
        try {
          await createSection(prepared.command);
        } catch (e) {
          showEditorFailure(caughtErrorMessage(e, "Could not add part."));
        }
        return;
      case "createSection":
        try {
          const newId = await createSection(prepared.command);
          setSelectedId(newId);
          setPendingPublication(null);
          setLogPrefill("insertion");
        } catch (e) {
          showEditorFailure(caughtErrorMessage(e, "Could not add section."));
        }
        return;
      case "renameSection":
        try {
          await rename(prepared.command);
        } catch (e) {
          showEditorFailure(caughtErrorMessage(e, "Could not rename section."));
        }
        return;
      case "defineTerm":
        try {
          await defineTerm(prepared.command);
        } catch (e) {
          showEditorFailure(caughtErrorMessage(e, "Could not save definition."));
        }
        return;
      case "draftAmendment":
        try {
          const r = await draftAmendment(prepared.command);
          setDraft(textDoc(r.amendedText));
          showEditorSuccess("AI draft applied. Review and save it when ready.");
        } catch (e) {
          showEditorFailure(caughtErrorMessage(e, "Could not apply amendment."));
        }
        return;
      case "deleteLegislation":
        try {
          await removeLegislation(prepared.command);
          navigate("/editor");
        } catch (e) {
          showEditorFailure(caughtErrorMessage(e, "Delete failed"));
        }
        return;
    }
  };

  const saveClean = async () => {
    if (selected === null) return;
    if (docToPlainText(baseline.current).trim() !== "" && !window.confirm("Save this as an editorial correction without publishing a client update?")) return;
    try {
      await updateContent({ id: selected._id, content: draft });
      baseline.current = draft;
      setPendingPublication(null);
      setLogPrefill(null);
      showEditorSuccess("Editorial correction saved without a client update.");
    } catch (error) {
      showEditorFailure(caughtErrorMessage(error, "Could not save the editorial correction."));
    }
  };

  const preparePublication = (changeType: "amended" | "insertion") => {
    if (selected === null || selected.nodeType !== "section") return;
    const markup = changeType === "amended" ? buildSectionMarkup(baseline.current, draft) : null;
    if (markup !== null && !markup.hasChanges) {
      showEditorFailure("No changes were found to publish as an amendment.");
      return;
    }
    setPendingPublication({
      sectionId: selected._id,
      content: draft,
      hidden: false,
      changeType,
      markup: markup?.doc ?? null,
    });
    setLogPrefill(changeType);
    showEditorSuccess(
      changeType === "amended"
        ? "Amendment prepared. Complete the publication details on the right."
        : "Insertion prepared. Complete the publication details on the right.",
    );
  };

  const runExtract = async () => {
    setStructureExtractState((current) => nextLegislationStructureExtractState(current, { type: "extractStarted" }));
    try {
      const r = await extractStructure({ sourceText: aiText });
      setStructureExtractState((current) => nextLegislationStructureExtractState(current, {
        type: "extractSucceeded",
        nodes: r.nodes,
      }));
    } catch (e) {
      setStructureExtractState((current) => nextLegislationStructureExtractState(current, {
        type: "extractFailed",
        error: caughtErrorMessage(e, "Could not apply amendment."),
      }));
    }
  };

  const insertExtracted = async () => {
    if (aiNodes === null) return;
    await bulkInsert({ legislationId: id, nodes: aiNodes });
    setPendingPublication(null);
    setLogPrefill("insertion");
    setStructureExtractState((current) => nextLegislationStructureExtractState(current, { type: "inserted" }));
  };

  const toggleHidden = async (node: SectionNode) => {
    if (node.hidden) {
      try {
        await setHidden({ id: node.id, hidden: false });
        showEditorSuccess("Section restored to the client reader.");
      } catch (error) {
        showEditorFailure(caughtErrorMessage(error, "Could not restore the section."));
      }
      return;
    }
    const section = sections.find((candidate) => candidate._id === node.id && candidate.nodeType === "section");
    if (section === undefined) return;
    const content = richTextDocOrEmpty(section.content);
    setSelectedId(section._id);
    setDraft(content);
    baseline.current = content;
    setPendingPublication({ sectionId: section._id, content, hidden: true, changeType: "repeal", markup: null });
    setLogPrefill("repeal");
    showEditorSuccess("Repeal prepared. The section remains visible to clients until publication details are completed.");
  };

  const selectSection = (sectionId: Id<"sections">) => {
    if (sectionId === selectedId) return;
    if (hasUnpublishedWork && !window.confirm("Discard the unpublished changes for this section?")) return;
    setPendingPublication(null);
    const target = sections.find((section) => section._id === sectionId);
    const isUnpublishedInsertion = target?.nodeType === "section" && target.hidden && docToPlainText(richTextDocOrEmpty(target.content)).trim() === "";
    setLogPrefill(isUnpublishedInsertion ? "insertion" : null);
    setSelectedId(sectionId);
  };

  const aiDraft = () => {
    if (selected === null) return;
    updateEditorDialog({ type: "openDraftAmendment", currentText: docToPlainText(draft) });
  };

  return (
    <div className="mx-auto max-w-[1180px] space-y-4 px-6 py-7 lg:px-9">
      <div className="flex items-center justify-between">
        <div>
          <Link className="text-sm text-brand hover:underline" to="/editor">← All legislation</Link>
          <h1 className="mt-1 text-xl font-semibold">{legislation.title}</h1>
          <p className="text-xs text-slate-400">{legislation.reference}</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setStructureExtractState((current) => nextLegislationStructureExtractState(current, { type: "toggleOpen" }))} className="rounded border border-[color:var(--accent)]/40 px-3 py-1.5 text-sm text-[var(--accent-dark)] hover:bg-[var(--accent-soft)]">
            ✦ AI: extract structure
          </button>
          <button type="button" onClick={addPart} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100">
            + Part
          </button>
          <button
            type="button"
            onClick={() => updateEditorDialog({ type: "openDeleteLegislation", title: legislation.title })}
            className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
          >
            Delete
          </button>
        </div>
      </div>

      {editorFeedback.kind === "visible" && (
        <div className={legislationEditorFeedbackClassName(editorFeedback)}>
          <span>{editorFeedback.message}</span>
          <button type="button" onClick={clearEditorFeedback} className="shrink-0 text-xs font-medium underline-offset-2 hover:underline">
            Dismiss
          </button>
        </div>
      )}

      <EditorCommandDialog state={editorDialog} onAction={updateEditorDialog} onSubmit={submitEditorDialog} />

      {aiOpen && (
        <div className="rounded-lg border border-[color:var(--accent)]/30 bg-[var(--accent-soft)]/40 p-4">
          <p className="mb-2 text-sm font-medium text-slate-700">
            Paste the legislation's source text — Claude extracts the Part / Division / Section outline (draft, you review before inserting).
          </p>
          <textarea
            aria-label="Legislation source text for structure extraction"
            value={aiText}
            onChange={(e) => setStructureExtractState((current) => nextLegislationStructureExtractState(current, { type: "sourceTextInput", value: e.target.value }))}
            placeholder="Paste Act / Regulation text here…"
            className="h-40 w-full rounded border border-slate-300 p-2 font-mono text-xs"
          />
          <div className="mt-2 flex items-center gap-2">
            <button type="button" disabled={extractButton.disabled} onClick={runExtract} className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-40">
              {extractButton.label}
            </button>
            {aiNodes !== null && (
              <button type="button" onClick={insertExtracted} className="rounded border border-emerald-400 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50">
                {insertStructureNodesButtonLabel(aiNodes.length)}
              </button>
            )}
            {aiError !== null && <span className="text-sm text-red-600">{aiError}</span>}
          </div>
          {aiNodes !== null && (
            <ul className="mt-3 max-h-48 overflow-y-auto rounded border border-slate-200 bg-white p-2 text-sm">
              {aiNodes.map((n, i) => {
                const item = structurePreviewNodeView(n);
                return (
                  <li key={i} style={{ paddingLeft: item.paddingLeft }} className={item.className}>
                    {item.label}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[20rem_1fr_18rem]">
        {/* Structure tree */}
        <aside className="rounded-lg border border-slate-200 bg-white p-2 text-sm">
          {tree.length === 0 ? (
            <p className="p-2 text-slate-400">No structure yet. Add a Part.</p>
          ) : (
            <StructureTree
              nodes={tree}
              selectedId={selectedId}
              onSelect={selectSection}
              onAddSection={addSection}
              onRename={handleRename}
              onToggleHidden={toggleHidden}
              onMove={move}
            />
          )}
        </aside>

        {/* Section editor */}
        <section className="rounded-lg border border-slate-200 bg-white p-4">
          {selected === null ? (
            <p className="text-slate-400">Select a section to edit, or add one to a Part.</p>
          ) : selected.nodeType !== "section" ? (
            <p className="text-slate-400">"{selected.title}" is a container. Select a leaf section to edit content.</p>
          ) : (
            <div className="space-y-3">
              <h2 className="font-semibold">{selected.number}. {selected.title}</h2>
              <SectionEditor key={selected._id} value={draft} onChange={setDraft} onDefineTerm={handleDefineTerm} />
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={saveClean} className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100">
                  Save editorial correction
                </button>
                {selected.hidden && logPrefill === "insertion" && (
                  <button type="button" onClick={() => preparePublication("insertion")} className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700">
                    Prepare insertion
                  </button>
                )}
                <button type="button" onClick={() => preparePublication("amended")} className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark">
                  Prepare amendment
                </button>
                <button type="button" onClick={aiDraft} className="rounded border border-[color:var(--accent)]/40 px-3 py-1.5 text-sm text-[var(--accent-dark)] hover:bg-[var(--accent-soft)]">
                  ✦ AI draft
                </button>
                <span className="self-center text-xs text-slate-400">
                  Preparing a change does not alter client-visible text until publication details are completed.
                </span>
              </div>

              {pendingPublication !== null && (
                <div className="rounded border border-amber-200 bg-amber-50/60 p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">{changeTypeDisplayName(pendingPublication.changeType)} ready to publish</span>
                    <button type="button" onClick={() => setPendingPublication(null)} className="text-xs text-red-600 hover:underline">Discard</button>
                  </div>
                  {pendingPublication.markup !== null && (
                    <div className="text-sm"><RichText doc={pendingPublication.markup} /></div>
                  )}
                  <p className="mt-2 text-xs text-amber-700">
                    Complete the publication details on the right. Content and the audit record will be committed together.
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Right rail: defined terms + update log */}
        <aside className="space-y-4">
          <DefinedTermsPanel terms={terms ?? []} onRemove={(tid) => removeTerm({ id: tid })} />
          <UpdateLogPanel
            legislationId={id}
            updates={updates ?? []}
            selectedSectionId={selected?.nodeType === "section" ? selected._id : null}
            prefill={logPrefill}
            pendingPublication={pendingPublication}
            linkedAlert={linkedAlert ?? null}
            alertId={alertId}
            onDone={() => {
              if (pendingPublication !== null) {
                baseline.current = pendingPublication.content;
                setDraft(pendingPublication.content);
              }
              setLogPrefill(null);
              setPendingPublication(null);
              showEditorSuccess("Change published with its update record.");
            }}
          />
        </aside>
      </div>
    </div>
  );
}

function EditorCommandDialog({
  state,
  onAction,
  onSubmit,
}: {
  state: LegislationEditorDialogState<Id<"sections">>;
  onAction: (action: LegislationEditorDialogAction<Id<"sections">>) => void;
  onSubmit: () => void;
}) {
  if (state.kind === "closed") return null;

  let body: React.ReactNode;
  switch (state.kind) {
    case "addPart":
      body = (
        <DialogTextField
          label="Part title"
          value={state.title}
          onChange={(value) => onAction({ type: "titleInput", value })}
          placeholder="Part 1 - Preliminary"
          autoFocus
        />
      );
      break;
    case "addSection":
      body = (
        <>
          <DialogTextField
            label="Section number"
            value={state.number}
            onChange={(value) => onAction({ type: "numberInput", value })}
            placeholder="51"
            autoFocus
          />
          <DialogTextField
            label="Section title"
            value={state.title}
            onChange={(value) => onAction({ type: "titleInput", value })}
            placeholder="Duties"
          />
        </>
      );
      break;
    case "renameSection":
      body = (
        <>
          <DialogTextField
            label="Number"
            value={state.number}
            onChange={(value) => onAction({ type: "numberInput", value })}
            autoFocus
          />
          <DialogTextField
            label="Title"
            value={state.title}
            onChange={(value) => onAction({ type: "titleInput", value })}
          />
        </>
      );
      break;
    case "defineTerm":
      body = (
        <>
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {state.term}
          </div>
          <DialogTextArea
            label="Definition"
            value={state.definition}
            onChange={(value) => onAction({ type: "definitionInput", value })}
            autoFocus
          />
        </>
      );
      break;
    case "draftAmendment":
      body = (
        <DialogTextArea
          label="Amendment instruction"
          value={state.instruction}
          onChange={(value) => onAction({ type: "instructionInput", value })}
          placeholder="Describe what should change"
          autoFocus
        />
      );
      break;
    case "deleteLegislation":
      body = (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {legislationDeleteConfirmationMessage(state.title)}
        </p>
      );
      break;
  }

  const isDelete = state.kind === "deleteLegislation";
  const submitClassName = isDelete
    ? "rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
    : "rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/35 px-4 py-8">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby="legislation-editor-dialog-title"
        className="w-full max-w-md space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-xl"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <h2 id="legislation-editor-dialog-title" className="text-[15px] font-semibold text-slate-900">
            {legislationEditorDialogTitle(state)}
          </h2>
          <button type="button" onClick={() => onAction({ type: "closed" })} className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100">
            Close
          </button>
        </div>
        <div className="space-y-3">{body}</div>
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <button type="button" onClick={() => onAction({ type: "closed" })} className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button type="submit" className={submitClassName}>
            {legislationEditorDialogSubmitLabel(state)}
          </button>
        </div>
      </form>
    </div>
  );
}

function DialogTextField({
  label,
  value,
  onChange,
  placeholder,
  autoFocus = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="block space-y-1 text-[12.5px] font-medium text-slate-600">
      <span>{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-normal text-slate-800 focus:border-brand focus:outline-none"
      />
    </label>
  );
}

function DialogTextArea({
  label,
  value,
  onChange,
  placeholder,
  autoFocus = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <label className="block space-y-1 text-[12.5px] font-medium text-slate-600">
      <span>{label}</span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="min-h-28 w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-normal text-slate-800 focus:border-brand focus:outline-none"
      />
    </label>
  );
}

function StructureTree({
  nodes,
  selectedId,
  onSelect,
  onAddSection,
  onRename,
  onToggleHidden,
  onMove,
  depth = 0,
}: {
  nodes: readonly SectionNode[];
  selectedId: Id<"sections"> | null;
  onSelect: (id: Id<"sections">) => void;
  onAddSection: (parentId: Id<"sections">) => void;
  onRename: (n: SectionNode) => void;
  onToggleHidden: (n: SectionNode) => void;
  onMove: (n: SectionNode, dir: -1 | 1) => void;
  depth?: number;
}) {
  return (
    <ul>
      {nodes.map((n) => {
        const row = sectionTreeRowView({ node: n, selectedId, depth });
        return (
          <li key={n.id}>
            <div
              style={{ paddingLeft: `${row.paddingLeft}px` }}
              className={row.rowClassName}
            >
              <button type="button" onClick={() => onSelect(n.id)} className={row.buttonClassName}>
                {row.label}
              </button>
              <span className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
                <IconBtn title="Move up" onClick={() => onMove(n, -1)}>↑</IconBtn>
                <IconBtn title="Move down" onClick={() => onMove(n, 1)}>↓</IconBtn>
                <IconBtn title="Rename" onClick={() => onRename(n)}>✎</IconBtn>
                {row.isContainer && <IconBtn title="Add section" onClick={() => onAddSection(n.id)}>＋</IconBtn>}
                <IconBtn title={row.visibilityToggleTitle} onClick={() => onToggleHidden(n)}>{row.visibilityToggleIcon}</IconBtn>
              </span>
            </div>
            {n.children.length > 0 && (
              <StructureTree nodes={n.children} selectedId={selectedId} onSelect={onSelect} onAddSection={onAddSection} onRename={onRename} onToggleHidden={onToggleHidden} onMove={onMove} depth={depth + 1} />
            )}
          </li>
        );
      })}
    </ul>
  );
}

const IconBtn = ({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) => (
  <button type="button" title={title} onClick={onClick} className="rounded px-1 text-xs text-slate-500 hover:bg-slate-200 hover:text-slate-800">
    {children}
  </button>
);

function DefinedTermsPanel({ terms, onRemove }: { terms: Doc<"definedTerms">[]; onRemove: (id: Id<"definedTerms">) => void }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <h3 className="mb-2 text-sm font-semibold">Defined terms ({terms.length})</h3>
      {terms.length === 0 ? (
        <p className="text-xs text-slate-400">Select text in the editor and click “Define term”.</p>
      ) : (
        <ul className="space-y-1">
          {terms.map((t) => (
            <li key={t._id} className="flex items-center justify-between text-sm">
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs">{t.term}</span>
              <button type="button" onClick={() => onRemove(t._id)} className="text-xs text-red-600 hover:underline">remove</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UpdateLogPanel({
  legislationId,
  updates,
  selectedSectionId,
  prefill,
  pendingPublication,
  linkedAlert,
  alertId,
  onDone,
}: {
  legislationId: Id<"legislation">;
  updates: Doc<"updateLog">[];
  selectedSectionId: Id<"sections"> | null;
  prefill: ChangeType | null;
  pendingPublication: PendingPublication | null;
  linkedAlert: Doc<"legislativeAlerts"> | null;
  alertId: Id<"legislativeAlerts"> | null;
  onDone: () => void;
}) {
  const publishChange = useMutation(api.updateLog.publishSectionChange);
  const [formState, setFormState] = useState(() => emptyUpdateLogFormState());
  const [submission, setSubmission] = useState(() => emptyUpdateLogSubmissionState());
  const [summary, setSummary] = useState("");
  const submissionError = updateLogSubmissionError(submission);

  const updateFormState = (action: UpdateLogFormAction) => {
    setFormState((current) => nextUpdateLogFormState(current, action));
    setSubmission((current) => nextUpdateLogSubmissionState(current, { type: "cleared" }));
  };

  useEffect(() => {
    if (prefill === null) return;
    setFormState((current) => nextUpdateLogFormState(current, { type: "changeTypeInput", value: prefill }));
    setSubmission((current) => nextUpdateLogSubmissionState(current, { type: "cleared" }));
  }, [prefill]);

  useEffect(() => {
    if (linkedAlert === null || linkedAlert.legislationId !== legislationId || linkedAlert.updateLogId !== null) return;
    setFormState((current) => {
      const withTitle = nextUpdateLogFormState(current, { type: "amendingLegislationInput", value: linkedAlert.amendingLegislation });
      const withDate = linkedAlert.commencementDate === null
        ? withTitle
        : nextUpdateLogFormState(withTitle, { type: "commencementDateInput", value: dateInputValueFromMs(linkedAlert.commencementDate) });
      return linkedAlert.changeType === null ? withDate : nextUpdateLogFormState(withDate, { type: "changeTypeInput", value: linkedAlert.changeType });
    });
    setSummary(linkedAlert.summary ?? "");
  }, [legislationId, linkedAlert]);

  const formView = useMemo(
    () => updateLogFormView({
      legislationId,
      selectedSectionId,
      amendingLegislation: formState.amendingLegislation,
      commencementDate: formState.commencementDate,
      changeType: pendingPublication?.changeType ?? formState.changeType,
      summary,
      markup: pendingPublication?.markup ?? null,
    }),
    [formState, legislationId, pendingPublication, selectedSectionId, summary],
  );

  const submit = async () => {
    if (!formView.canSubmit || submission.kind === "submitting") return;
    if (pendingPublication === null || selectedSectionId !== pendingPublication.sectionId) {
      setSubmission((current) => nextUpdateLogSubmissionState(current, {
        type: "failed",
        error: "Prepare an amendment, insertion or repeal before publishing.",
      }));
      return;
    }
    if (!formView.prepared.ok) {
      setSubmission((current) => nextUpdateLogSubmissionState(current, {
        type: "failed",
        error: formView.errorMessage ?? "Could not add update log entry.",
      }));
      return;
    }
    setSubmission((current) => nextUpdateLogSubmissionState(current, { type: "started" }));
    try {
      const prepared = formView.prepared.value;
      await publishChange({
        legislationId,
        sectionId: pendingPublication.sectionId,
        content: pendingPublication.content,
        hidden: pendingPublication.hidden,
        amendingLegislation: prepared.amendingLegislation,
        commencementDate: prepared.commencementDate,
        changeType: pendingPublication.changeType,
        ...(prepared.summary === undefined ? {} : { summary: prepared.summary }),
        ...(pendingPublication.markup === null ? {} : { markup: pendingPublication.markup }),
        ...(alertId !== null && linkedAlert?.legislationId === legislationId ? { alertId } : {}),
      });
      setSummary("");
      setFormState((current) => updateLogFormSubmittedState(current));
      setSubmission((current) => nextUpdateLogSubmissionState(current, { type: "succeeded" }));
      onDone();
    } catch (e) {
      setSubmission((current) => nextUpdateLogSubmissionState(current, {
        type: "failed",
        error: caughtErrorMessage(e, "Could not publish the section change."),
      }));
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <h3 className="mb-2 text-sm font-semibold">Publish change</h3>
      {linkedAlert !== null && linkedAlert.legislationId === legislationId && (
        <div className="mb-3 rounded border border-sky-200 bg-sky-50 px-2.5 py-2 text-xs text-sky-800">
          Linked alert: {linkedAlert.amendingLegislation}
        </div>
      )}
      {pendingPublication === null && <p className="mb-3 text-xs text-slate-400">Prepare a change in the editor before publishing.</p>}
      <div className="space-y-2">
        <label className="grid gap-1 text-xs font-medium text-slate-600">
          <span>Amending instrument</span>
          <input className="w-full rounded border border-slate-300 px-2 py-1 text-sm" value={formState.amendingLegislation} onChange={(e) => updateFormState({ type: "amendingLegislationInput", value: e.target.value })} />
        </label>
        <div className="flex gap-2">
          <label className="grid flex-1 gap-1 text-xs font-medium text-slate-600">
            <span>Commencement date</span>
            <input type="date" className="w-full rounded border border-slate-300 px-2 py-1 text-sm" value={formState.commencementDate} onChange={(e) => updateFormState({ type: "commencementDateInput", value: e.target.value })} />
          </label>
          <label className="grid flex-1 gap-1 text-xs font-medium text-slate-600">
            <span>Change type</span>
            <select disabled={pendingPublication !== null} className="w-full rounded border border-slate-300 px-2 py-1 text-sm disabled:bg-slate-50" value={pendingPublication?.changeType ?? formState.changeType} onChange={(e) => updateFormState({ type: "changeTypeInput", value: e.target.value })}>
              {CHANGE_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{changeTypeDisplayName(option)}</option>)}
            </select>
          </label>
        </div>
        <label className="grid gap-1 text-xs font-medium text-slate-600">
          <span>Plain-English summary</span>
          <textarea className="min-h-20 w-full rounded border border-slate-300 px-2 py-1 text-sm font-normal" value={summary} onChange={(event) => setSummary(event.target.value)} />
        </label>
        <button type="button" onClick={submit} disabled={pendingPublication === null || !formView.canSubmit || submission.kind === "submitting"} className="w-full rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-40">
          {submission.kind === "submitting" ? "Publishing..." : "Publish change"}
        </button>
        {submissionError !== null && <div className="text-[12.5px] text-red-600">{submissionError}</div>}
      </div>
      {updates.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="text-xs font-semibold text-slate-600">Published history ({updates.length})</div>
          <ul className="mt-2 space-y-1 text-xs">
          {updates.slice(0, 8).map((u) => (
            <li key={u._id} className="flex items-center justify-between">
              <span className="truncate">{u.amendingLegislation}</span>
              <span className="ml-2 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-slate-500">{u.changeType}</span>
            </li>
          ))}
          </ul>
        </div>
      )}
    </div>
  );
}
